import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hardcoded fallback rates so prices always convert even when /api/fx/rates is
// unreachable (e.g. before the network call resolves, or on a misconfigured
// production environment). These are conservative mid-market estimates and
// will be overwritten the moment live rates arrive.
const FALLBACK_RATES = {
  GBP: 1,
  USD: 1.27, EUR: 1.17, CAD: 1.72, AUD: 1.92, NZD: 2.10, CHF: 1.12,
  JPY: 192, CNY: 9.10, HKD: 9.92, SGD: 1.71, MYR: 5.95, THB: 44.5,
  IDR: 20100, PHP: 73.5, KRW: 1745, INR: 105.0, PKR: 354.0, BDT: 151.0,
  LKR: 374.0, NPR: 170.0,
  SAR: 4.76, AED: 4.66, QAR: 4.62, BHD: 0.477, OMR: 0.488, KWD: 0.389,
  JOD: 0.900, EGP: 63.5, TRY: 44.0, ZAR: 23.5,
};

const CURRENCY_SYMBOLS = {
  GBP: '£', USD: '$', EUR: '€', SAR: 'SAR', AED: 'AED',
  PKR: '₨', INR: '₹', BDT: '৳', CAD: 'C$', AUD: 'A$',
  SGD: 'S$', MYR: 'RM', QAR: 'QAR', KWD: 'KWD', JPY: '¥',
  CNY: '¥', THB: '฿', IDR: 'Rp', PHP: '₱', KRW: '₩',
  CHF: 'CHF', HKD: 'HK$', TRY: '₺', ZAR: 'R', NZD: 'NZ$',
};

// Currencies traditionally displayed without decimal places
const NO_DECIMAL_CURRENCIES = new Set(['JPY', 'KRW', 'IDR', 'VND', 'PKR', 'BDT', 'NPR']);

const STORAGE_KEY = 'flight380_currency';
const MANUAL_FLAG = 'flight380_currency_manual';
const COUNTRY_KEY = 'flight380_country';

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'GBP';
    } catch {
      return 'GBP';
    }
  });
  const [countryCode, setCountryCode] = useState(() => {
    try {
      return localStorage.getItem(COUNTRY_KEY) || null;
    } catch {
      return null;
    }
  });
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [ratesLoaded, setRatesLoaded] = useState(false);
  const [autoDetected, setAutoDetected] = useState(null);

  const setCurrency = useCallback((next) => {
    if (!next) return;
    const normalised = String(next).toUpperCase();
    setCurrencyState(normalised);
    try {
      localStorage.setItem(STORAGE_KEY, normalised);
      // Once the user chooses a currency manually, never auto-suggest again
      // for this browser. Departure-city based suggestions become a one-time
      // hint rather than a repeated prompt.
      localStorage.setItem(MANUAL_FLAG, '1');
    } catch (_) { /* ignore */ }
  }, []);

  // First-load IP-based location + currency auto-detection.
  // Currency: only auto-switches when the visitor hasn't picked one yet or set
  //           the manual flag (so returning users keep their previous choice).
  // Country flag: always resolves on first load if not already cached, even
  //           when the visitor has a currency preference, so the flag chip
  //           always has something to render.
  useEffect(() => {
    let cancelled = false;
    let hasStoredCurrency = false;
    let isManual = false;
    let hasStoredCountry = false;
    try {
      hasStoredCurrency = !!localStorage.getItem(STORAGE_KEY);
      isManual = localStorage.getItem(MANUAL_FLAG) === '1';
      hasStoredCountry = !!localStorage.getItem(COUNTRY_KEY);
    } catch (_) { /* localStorage blocked → still try to detect */ }

    // Nothing to fetch: currency is already frozen AND country flag is cached
    if ((hasStoredCurrency || isManual) && hasStoredCountry) return;

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/umrah-builder/currencies/detect`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data?.success) return;

        // Always cache the resolved country_code so the flag chip persists
        // across sessions once we know it.
        const cc = String(data.country_code || '').toUpperCase();
        if (cc && !hasStoredCountry) {
          setCountryCode(cc);
          try { localStorage.setItem(COUNTRY_KEY, cc); } catch (_) { /* ignore */ }
        }

        // Auto-switch currency only if the visitor hasn't chosen one already
        if (hasStoredCurrency || isManual) return;

        const suggested = String(data.suggested_currency || '').toUpperCase();
        if (!suggested || suggested === 'GBP' || data.source === 'fallback') return;
        // Silently switch the visitor to their local currency and remember
        // the detection so we don't fire the same lookup on every reload.
        setCurrencyState(suggested);
        setAutoDetected({
          code: suggested,
          symbol: data.currency_symbol,
          name: data.currency_name,
          country: data.country_name,
          countryCode: cc,
        });
        try {
          localStorage.setItem(STORAGE_KEY, suggested);
          // IP-based detection is a stronger signal than departure-airport
          // heuristics — mark this as a "manual" preference so the older
          // airport-based suggestion dialog doesn't immediately fight it.
          localStorage.setItem(MANUAL_FLAG, '1');
        } catch (_) { /* ignore */ }
        // One-time friendly toast so travellers know why prices changed and
        // can revert to GBP with a single tap.
        try {
          toast(`Showing prices in ${data.currency_symbol} ${suggested}`, {
            description: `Detected from your location${data.country_name ? ` (${data.country_name})` : ''}. Tap to switch back to £ GBP.`,
            duration: 8000,
            action: {
              label: 'Use £ GBP',
              onClick: () => {
                setCurrencyState('GBP');
                try {
                  localStorage.setItem(STORAGE_KEY, 'GBP');
                  localStorage.setItem(MANUAL_FLAG, '1');
                } catch (_) { /* ignore */ }
              },
            },
          });
        } catch (_) { /* toast is best-effort */ }
      } catch (_) { /* silent — GBP default keeps working */ }
    })();

    return () => { cancelled = true; };
  }, []);

  // Fetch GBP-based rates once on mount; cached server-side for 12h
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/fx/rates?base=GBP`);
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        if (data && data.success && data.rates && typeof data.rates === 'object') {
          // Merge live rates over fallbacks so anything not returned still works
          setRates({ ...FALLBACK_RATES, ...data.rates, GBP: 1 });
          setRatesLoaded(true);
        }
      } catch (e) {
        // Keep fallback rates – at least conversions stay sane
        // eslint-disable-next-line no-console
        console.warn('Currency rates load failed, using fallback rates:', e?.message || e);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const symbolFor = useCallback((code) => CURRENCY_SYMBOLS[code] || `${code} `, []);

  // Convert a GBP amount to the selected currency.
  const convertGBP = useCallback((amountGBP) => {
    const num = Number(amountGBP) || 0;
    if (currency === 'GBP') return num;
    const rate = rates?.[currency];
    if (!rate || !isFinite(rate)) return num; // graceful fallback
    return num * rate;
  }, [currency, rates]);

  // Format a GBP amount in the currently selected currency.
  // Keeps the displayed number compact (no forced .00) so mobile layouts
  // that previously fit `£100` don't break with `$136.11`.
  const formatGBP = useCallback((amountGBP) => {
    const converted = convertGBP(amountGBP);
    const abs = Math.abs(converted);
    let maxFractionDigits;
    if (NO_DECIMAL_CURRENCIES.has(currency) || abs >= 1000) {
      maxFractionDigits = 0;             // big numbers: drop decimals
    } else if (abs >= 100) {
      maxFractionDigits = 0;             // 100+ : whole units only (compact)
    } else if (abs >= 10) {
      maxFractionDigits = 1;
    } else {
      maxFractionDigits = 2;
    }
    const formatted = converted.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxFractionDigits,
    });
    const sym = symbolFor(currency);
    const needsSpace = sym.length > 1;
    return `${sym}${needsSpace ? ' ' : ''}${formatted}`;
  }, [convertGBP, currency, symbolFor]);

  const value = useMemo(() => ({
    currency,
    setCurrency,
    rates,
    ratesLoaded,
    symbol: symbolFor(currency),
    convertGBP,
    formatGBP,
    autoDetected,
    countryCode,
  }), [currency, setCurrency, rates, ratesLoaded, symbolFor, convertGBP, formatGBP, autoDetected, countryCode]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Safe fallback when used outside provider (shouldn't happen in normal flow)
    return {
      currency: 'GBP',
      setCurrency: () => {},
      rates: FALLBACK_RATES,
      ratesLoaded: true,
      symbol: '£',
      convertGBP: (x) => Number(x) || 0,
      formatGBP: (x) => `£${(Number(x) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
      autoDetected: null,
      countryCode: null,
    };
  }
  return ctx;
};

/**
 * <Money gbp={123} /> – renders the given GBP amount in the user's selected currency.
 * Auto re-renders whenever currency changes anywhere in the app.
 */
export const Money = ({ gbp = 0, className, fallback = '—' }) => {
  const { formatGBP } = useCurrency();
  const numeric = Number(gbp);
  if (!isFinite(numeric)) return <span className={className}>{fallback}</span>;
  return (
    <span
      className={['whitespace-nowrap tabular-nums', className].filter(Boolean).join(' ')}
      data-testid="money-display"
    >
      {formatGBP(numeric)}
    </span>
  );
};

export default CurrencyContext;
